/**
 * Timer/Stopwatch module for whiteboard
 */

let timerInterval = null;
let timeInSeconds = 0;
let isRunning = false;
let isTimerMode = true; // true = countdown timer, false = stopwatch
let initialTime = 60; // default 1 minute for timer mode
let isCompactMode = false; // compact mode shows only time

// Preset times in seconds
const PRESETS = [60, 180, 300, 600]; // 1, 3, 5, 10 minutes

/**
 * Initialize timer widget
 */
export function initTimer() {
    createTimerWidget();
    setupEventListeners();
    updateDisplay();
}

/**
 * Create the timer widget HTML
 */
function createTimerWidget() {
    // Prevent duplicate widget creation
    if (document.getElementById('timer-widget')) {
        return;
    }
    
    const widget = document.createElement('div');
    widget.id = 'timer-widget';
    widget.className = 'timer-widget';
    widget.innerHTML = `
        <div class="timer-header">
            <span class="timer-drag-handle">⋮⋮</span>
            <div class="timer-mode-toggle timer-full-only">
                <button class="timer-mode-btn active" data-mode="timer">計時器</button>
                <button class="timer-mode-btn" data-mode="stopwatch">碼錶</button>
            </div>
            <button class="timer-compact-btn" title="切換簡潔模式">
                <svg viewBox="0 0 24 24" width="16" height="16">
                    <path fill="currentColor" d="M19 13H5v-2h14v2z"/>
                </svg>
            </button>
            <button class="timer-close-btn" title="關閉">×</button>
        </div>
        <div class="timer-display">
            <span class="timer-time">00:00</span>
            <div class="timer-compact-controls">
                <button class="timer-btn timer-compact-play" title="開始/暫停">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                        <path fill="currentColor" d="M8 5v14l11-7z"/>
                    </svg>
                </button>
            </div>
        </div>
        <div class="timer-presets timer-full-only">
            <button class="timer-preset-btn" data-seconds="60">1分</button>
            <button class="timer-preset-btn" data-seconds="180">3分</button>
            <button class="timer-preset-btn" data-seconds="300">5分</button>
            <button class="timer-preset-btn" data-seconds="600">10分</button>
        </div>
        <div class="timer-custom-input timer-full-only">
            <input type="number" class="timer-minutes-input" min="0" max="99" value="1" placeholder="分">
            <span>:</span>
            <input type="number" class="timer-seconds-input" min="0" max="59" value="0" placeholder="秒">
            <button class="timer-set-btn">設定</button>
        </div>
        <div class="timer-controls timer-full-only">
            <button class="timer-btn timer-start-btn" title="開始">
                <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M8 5v14l11-7z"/>
                </svg>
            </button>
            <button class="timer-btn timer-pause-btn" title="暫停" style="display:none;">
                <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
            </button>
            <button class="timer-btn timer-reset-btn" title="重置">
                <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
            </button>
        </div>
    `;
    document.body.appendChild(widget);
    
    // Make draggable
    makeDraggable(widget);
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    const widget = document.getElementById('timer-widget');
    if (!widget) return;
    
    // Mode toggle
    widget.querySelectorAll('.timer-mode-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const mode = btn.dataset.mode;
            setMode(mode === 'timer');
            widget.querySelectorAll('.timer-mode-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
        });
    });
    
    // Preset buttons
    widget.querySelectorAll('.timer-preset-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const seconds = parseInt(btn.dataset.seconds);
            setTime(seconds);
        });
    });
    
    // Custom time set
    widget.querySelector('.timer-set-btn').addEventListener('click', () => {
        const minutes = parseInt(widget.querySelector('.timer-minutes-input').value) || 0;
        const seconds = parseInt(widget.querySelector('.timer-seconds-input').value) || 0;
        setTime(minutes * 60 + seconds);
    });
    
    // Start button
    widget.querySelector('.timer-start-btn').addEventListener('click', start);
    
    // Pause button
    widget.querySelector('.timer-pause-btn').addEventListener('click', pause);
    
    // Reset button
    widget.querySelector('.timer-reset-btn').addEventListener('click', reset);
    
    // Close button
    widget.querySelector('.timer-close-btn').addEventListener('click', hideTimer);
    
    // Compact mode toggle
    widget.querySelector('.timer-compact-btn').addEventListener('click', toggleCompactMode);
    
    // Compact play/pause button
    widget.querySelector('.timer-compact-play').addEventListener('click', () => {
        if (isRunning) {
            pause();
        } else {
            start();
        }
    });
}

/**
 * Make element draggable
 */
function makeDraggable(element) {
    const handle = element.querySelector('.timer-drag-handle');
    let isDragging = false;
    let offsetX, offsetY;
    
    handle.addEventListener('mousedown', (e) => {
        isDragging = true;
        offsetX = e.clientX - element.offsetLeft;
        offsetY = e.clientY - element.offsetTop;
        element.style.cursor = 'grabbing';
    });
    
    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        element.style.left = (e.clientX - offsetX) + 'px';
        element.style.top = (e.clientY - offsetY) + 'px';
        element.style.right = 'auto';
    });
    
    document.addEventListener('mouseup', () => {
        isDragging = false;
        element.style.cursor = '';
    });
}

/**
 * Set timer/stopwatch mode
 */
function setMode(timerMode) {
    isTimerMode = timerMode;
    const widget = document.getElementById('timer-widget');
    const presets = widget.querySelector('.timer-presets');
    const customInput = widget.querySelector('.timer-custom-input');
    
    if (isTimerMode) {
        presets.style.display = 'flex';
        customInput.style.display = 'flex';
        timeInSeconds = initialTime;
    } else {
        presets.style.display = 'none';
        customInput.style.display = 'none';
        timeInSeconds = 0;
    }
    
    reset();
}

/**
 * Toggle compact mode
 */
function toggleCompactMode() {
    isCompactMode = !isCompactMode;
    const widget = document.getElementById('timer-widget');
    
    if (isCompactMode) {
        widget.classList.add('timer-compact');
        widget.querySelector('.timer-compact-btn').title = '展開完整模式';
        widget.querySelector('.timer-compact-btn svg').innerHTML = '<path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>';
    } else {
        widget.classList.remove('timer-compact');
        widget.querySelector('.timer-compact-btn').title = '切換簡潔模式';
        widget.querySelector('.timer-compact-btn svg').innerHTML = '<path fill="currentColor" d="M19 13H5v-2h14v2z"/>';
    }
    
    updateCompactPlayIcon();
}

/**
 * Update compact play button icon based on state
 */
function updateCompactPlayIcon() {
    const widget = document.getElementById('timer-widget');
    if (!widget) return;
    
    const compactPlay = widget.querySelector('.timer-compact-play svg');
    if (isRunning) {
        compactPlay.innerHTML = '<path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>';
    } else {
        compactPlay.innerHTML = '<path fill="currentColor" d="M8 5v14l11-7z"/>';
    }
}

/**
 * Set time for timer
 */
function setTime(seconds) {
    initialTime = seconds;
    timeInSeconds = seconds;
    updateDisplay();
}

/**
 * Start timer/stopwatch
 */
function start() {
    if (isRunning) return;
    isRunning = true;
    
    const widget = document.getElementById('timer-widget');
    widget.querySelector('.timer-start-btn').style.display = 'none';
    widget.querySelector('.timer-pause-btn').style.display = 'inline-flex';
    updateCompactPlayIcon();
    
    timerInterval = setInterval(() => {
        if (isTimerMode) {
            timeInSeconds--;
            if (timeInSeconds <= 0) {
                timeInSeconds = 0;
                pause();
                timerComplete();
            }
        } else {
            timeInSeconds++;
        }
        updateDisplay();
    }, 1000);
}

/**
 * Pause timer/stopwatch
 */
function pause() {
    isRunning = false;
    clearInterval(timerInterval);
    
    const widget = document.getElementById('timer-widget');
    widget.querySelector('.timer-start-btn').style.display = 'inline-flex';
    widget.querySelector('.timer-pause-btn').style.display = 'none';
    updateCompactPlayIcon();
}

/**
 * Reset timer/stopwatch
 */
function reset() {
    pause();
    timeInSeconds = isTimerMode ? initialTime : 0;
    updateDisplay();
    
    const widget = document.getElementById('timer-widget');
    widget.querySelector('.timer-display').classList.remove('timer-complete');
}

/**
 * Update display
 */
function updateDisplay() {
    const widget = document.getElementById('timer-widget');
    if (!widget) return;
    
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    const display = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    
    widget.querySelector('.timer-time').textContent = display;
    
    // Warning color when < 10 seconds in timer mode
    const displayEl = widget.querySelector('.timer-display');
    if (isTimerMode && timeInSeconds <= 10 && timeInSeconds > 0) {
        displayEl.classList.add('timer-warning');
    } else {
        displayEl.classList.remove('timer-warning');
    }
}

/**
 * Timer complete action
 */
function timerComplete() {
    const widget = document.getElementById('timer-widget');
    widget.querySelector('.timer-display').classList.add('timer-complete');
    
    // Play sound
    playAlertSound();
    
    // Flash animation
    flashWidget();
}

/**
 * Play alert sound
 */
function playAlertSound() {
    // Create a simple beep using Web Audio API
    try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 880; // A5 note
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        
        // Play 3 beeps
        setTimeout(() => playBeep(audioContext), 600);
        setTimeout(() => playBeep(audioContext), 1200);
    } catch (e) {
        console.log('Audio not supported');
    }
}

function playBeep(audioContext) {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 880;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
}

/**
 * Flash widget animation
 */
function flashWidget() {
    const widget = document.getElementById('timer-widget');
    let count = 0;
    const flash = setInterval(() => {
        widget.classList.toggle('timer-flash');
        count++;
        if (count >= 6) {
            clearInterval(flash);
            widget.classList.remove('timer-flash');
        }
    }, 300);
}

/**
 * Show timer widget
 */
export function showTimer() {
    const widget = document.getElementById('timer-widget');
    if (widget) {
        widget.style.display = 'flex';
    }
}

/**
 * Hide timer widget
 */
export function hideTimer() {
    const widget = document.getElementById('timer-widget');
    if (widget) {
        widget.style.display = 'none';
    }
    pause();
}

/**
 * Toggle timer visibility
 */
export function toggleTimer() {
    const widget = document.getElementById('timer-widget');
    if (!widget) return;
    
    // Use getComputedStyle to check actual visibility (handles CSS-set display:none)
    const computedStyle = window.getComputedStyle(widget);
    if (computedStyle.display !== 'none') {
        hideTimer();
    } else {
        showTimer();
    }
}

/**
 * Check if timer is visible
 */
export function isTimerVisible() {
    const widget = document.getElementById('timer-widget');
    if (!widget) return false;
    
    const computedStyle = window.getComputedStyle(widget);
    return computedStyle.display !== 'none';
}
